import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "@mui/material";
import { Nullable } from "../../../../types/nullable";
import { workLocationsAutoCompleteQuery } from "../../../../../features/customer/customerAPI";
import { WorkLocation } from "../../../../../features/customer/types/queries";

type SharedReturn = {
  workLocationOptions: WorkLocation[];
  onInputChange: ReturnType<typeof debounce>;
  isLoading: boolean;
};

type SingleReturn = SharedReturn & {
  workLocation: Nullable<WorkLocation>;
  onChange: (value: Nullable<WorkLocation>) => void;
};

type MultiReturn = SharedReturn & {
  workLocations: WorkLocation[];
  onChange: (value: WorkLocation[] | null) => void;
};

export function useWorkLocationAutocomplete(options: {
  multiple: true;
}): MultiReturn;
export function useWorkLocationAutocomplete(options?: {
  multiple?: false;
}): SingleReturn;
export function useWorkLocationAutocomplete(options?: {
  multiple?: boolean;
}): SingleReturn | MultiReturn {
  const isMultiple = options?.multiple ?? false;

  const [workLocation, setWorkLocation] =
    useState<Nullable<WorkLocation>>(null);
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [search, setSearch] = useState("");
  const [workLocationOptions, setWorkLocationOptions] = useState<
    WorkLocation[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (search) {
      setIsLoading(true);
      workLocationsAutoCompleteQuery({ term: search })
        .then((res) => {
          const sorted = res.sort(
            (a, b) =>
              a.customerOrgName?.localeCompare(b.customerOrgName || "") ||
              a.name.localeCompare(b.name),
          );
          setWorkLocationOptions(sorted);
        })
        .finally(() => setIsLoading(false));
    }
  }, [search]);

  const onInputChangeCallback = useCallback((value: string, reason: string) => {
    if (reason === "input") {
      setSearch(value);
    }
  }, []);

  const debouncedOnInputChange = useMemo(
    () => debounce(onInputChangeCallback, 400),
    [onInputChangeCallback],
  );

  const onChangeSingle = useCallback((value: Nullable<WorkLocation>) => {
    setWorkLocation(value);
  }, []);

  const onChangeMulti = useCallback((value: WorkLocation[] | null) => {
    setWorkLocations(value ?? []);
  }, []);

  const shared: SharedReturn = {
    workLocationOptions,
    onInputChange: debouncedOnInputChange,
    isLoading,
  };

  if (isMultiple) {
    return {
      ...shared,
      workLocations,
      onChange: onChangeMulti,
    };
  }

  return {
    ...shared,
    workLocation,
    onChange: onChangeSingle,
  };
}

export default useWorkLocationAutocomplete;
